// SPDX-FileCopyrightText: 2025 LiveKit, Inc.
//
// SPDX-License-Identifier: Apache-2.0
import type { AudioFrame } from '@livekit/rtc-node';
import { AsyncLocalStorage } from 'node:async_hooks';
import { ReadableStream } from 'node:stream/web';
import {
  LLM as InferenceLLM,
  STT as InferenceSTT,
  TTS as InferenceTTS,
  type LLMModels,
  type STTModelString,
  type TTSModelString,
} from '../inference/index.js';
import { type Instructions, ReadonlyChatContext } from '../llm/chat_context.js';
import { DuplexModel, type DuplexSession } from '../llm/duplex.js';
import { DuplexRealtimeAdapter, DuplexRealtimeSession } from '../llm/duplex_adapter.js';
import type { ChatMessage, FunctionCall } from '../llm/index.js';
import {
  type ChatChunk,
  ChatContext,
  LLM,
  RealtimeModel,
  type ToolChoice,
  ToolContext,
  type ToolContextLike,
  ToolError,
  toToolContext,
} from '../llm/index.js';
import { log } from '../log.js';
import type { STT, SpeechEvent } from '../stt/index.js';
import { StreamAdapter as STTStreamAdapter } from '../stt/index.js';
import { SentenceTokenizer as BasicSentenceTokenizer } from '../tokenize/basic/index.js';
import type { TTS } from '../tts/index.js';
import { SynthesizeStream, StreamAdapter as TTSStreamAdapter } from '../tts/index.js';
import { type FlushSentinel, USERDATA_TIMED_TRANSCRIPT } from '../types.js';
import { Event, Future, Task, toStream } from '../utils.js';
import type { VAD } from '../vad.js';
import { type AgentActivity, agentActivityStorage } from './agent_activity.js';
import type { AgentSession, ExpressiveOptions, TurnDetectionMode } from './agent_session.js';
import {
  type AgentCreateOptions,
  type AgentTaskCreateOptions,
  createAgentTaskV2,
  createAgentV2,
} from './agent_v2.js';
import type { UserTurnExceededEvent } from './events.js';
import type { TimedString } from './io.js';
import type { SpeechHandle } from './speech_handle.js';
import type { ToolHandlingOptions } from './tool_executor.js';
import type { TurnHandlingOptions } from './turn_config/turn_handling.js';
import { migrateTurnHandling } from './turn_config/utils.js';

export type {
  AgentContext,
  AgentCreateOptions,
  AgentHookNodeResult,
  AgentHooks,
  AgentTaskContext,
  AgentTaskCreateOptions,
} from './agent_v2.js';

// speechHandle identifies which SpeechHandle owns the current tool call, enabling
// SpeechHandle.waitForPlayout() to distinguish self-wait (deadlock) from waiting
// on a different handle scheduled inside the tool.
export const functionCallStorage = new AsyncLocalStorage<{
  functionCall?: FunctionCall;
  speechHandle?: SpeechHandle;
}>();
export const speechHandleStorage = new AsyncLocalStorage<SpeechHandle>();
const activityTaskInfoStorage = new WeakMap<Task<any>, _ActivityTaskInfo>();

type _ActivityTaskInfo = {
  functionCall: FunctionCall | null;
  speechHandle: SpeechHandle | null;
  inlineTask: boolean;
};

/** @internal */
export function _setActivityTaskInfo<T>(
  task: Task<T>,
  options: {
    functionCall?: FunctionCall | null;
    speechHandle?: SpeechHandle | null;
    inlineTask?: boolean;
  },
): void {
  const info = activityTaskInfoStorage.get(task) ?? {
    functionCall: null,
    speechHandle: null,
    inlineTask: false,
  };

  if (Object.hasOwn(options, 'functionCall')) {
    info.functionCall = options.functionCall ?? null;
  }
  if (Object.hasOwn(options, 'speechHandle')) {
    info.speechHandle = options.speechHandle ?? null;
  }
  if (Object.hasOwn(options, 'inlineTask')) {
    info.inlineTask = options.inlineTask ?? false;
  }

  activityTaskInfoStorage.set(task, info);
}

/** @internal */
export function _getActivityTaskInfo<T>(task: Task<T>): _ActivityTaskInfo | undefined {
  return activityTaskInfoStorage.get(task);
}
export const STOP_RESPONSE_SYMBOL = Symbol('StopResponse');

export class StopResponse extends Error {
  constructor() {
    super();
    this.name = 'StopResponse';

    Object.defineProperty(this, STOP_RESPONSE_SYMBOL, {
      value: true,
    });
  }
}

export function isStopResponse(value: unknown): value is StopResponse {
  return (
    value !== undefined &&
    value !== null &&
    typeof value === 'object' &&
    STOP_RESPONSE_SYMBOL in value
  );
}

export interface ModelSettings {
  /** The tool choice to use when calling the LLM. */
  toolChoice?: ToolChoice;
}

/**
 * Fields to change on an {@link Agent}, passed to {@link Agent.updateOptions}.
 *
 * Only the fields you pass are changed. Omitting a field and passing it as `undefined` mean
 * the same thing — leave the agent's current value alone — matching `is_given()` /
 * `NOT_GIVEN` in the Python framework. Pass `null` to disable a model on this agent, which
 * also overrides any session-level model.
 */
export interface AgentUpdateOptions {
  /** New STT model. Pass `null` to disable the agent STT and override any session STT. */
  stt?: STT | STTModelString | null;
  /** New VAD model. Pass `null` to disable the agent VAD and override any session VAD. */
  vad?: VAD | null;
  /**
   * New LLM model. Pass `null` to disable the agent LLM and override any session LLM.
   *
   * A {@link RealtimeModel} or {@link DuplexModel} can only be set while the agent is stopped.
   * Swapping to or from one while running throws because it replaces the whole pipeline.
   * Use `AgentSession.updateAgent()` for that.
   */
  llm?: LLM | RealtimeModel | DuplexModel | LLMModels | null;
  /** New TTS model. Pass `null` to disable the agent TTS and override any session TTS. */
  tts?: TTS | TTSModelString | null;
  /** Expressive TTS delivery. Pass `false` to override and disable the session setting. */
  expressive?: boolean | ExpressiveOptions;
}

export interface AgentOptions<UserData> {
  id?: string;
  instructions: string | Instructions;
  chatCtx?: ChatContext;
  tools?: ToolContextLike<UserData>;
  stt?: STT | STTModelString | null;
  vad?: VAD | null;
  llm?: LLM | RealtimeModel | DuplexModel | LLMModels | null;
  tts?: TTS | TTSModelString | null;
  /** Expressive TTS delivery. When set, overrides the session value for this agent. */
  expressive?: boolean | ExpressiveOptions;
  turnHandling?: TurnHandlingOptions;
  toolHandling?: ToolHandlingOptions;
  minConsecutiveSpeechDelay?: number;
  useTtsAlignedTranscript?: boolean;
  /** @deprecated use turnHandling.turnDetection instead */
  turnDetection?: TurnDetectionMode;
  /** @deprecated use turnHandling.interruption.enabled instead */
  allowInterruptions?: boolean;
}

export class Agent<UserData = any> {
  private _id: string;
  /** @internal */
  _stt?: STT | null;
  /** @internal */
  _vad?: VAD | null;
  /** @internal */
  _llm?: LLM | RealtimeModel | null;
  /** @internal */
  _tts?: TTS | null;
  /** @internal */
  _expressive?: boolean | ExpressiveOptions;
  private _turnHandling?: Partial<TurnHandlingOptions>;

  private _minConsecutiveSpeechDelay?: number;
  private _useTtsAlignedTranscript?: boolean;

  /** @internal */
  _agentActivity?: AgentActivity;

  /** @internal */
  _chatCtx: ChatContext;

  /** @internal */
  _instructions: string | Instructions;

  /** @internal */
  _toolCtx: ToolContext<UserData>;

  /** @internal */
  _asyncToolOptions?: ToolHandlingOptions['asyncOptions'];

  static create<UserData = unknown>(options: AgentCreateOptions<UserData>): Agent<UserData> {
    return createAgentV2(Agent, options);
  }

  constructor({
    id,
    instructions,
    chatCtx,
    tools,
    turnDetection,
    stt,
    vad,
    llm,
    tts,
    expressive,
    allowInterruptions,
    turnHandling,
    toolHandling,
    minConsecutiveSpeechDelay,
    useTtsAlignedTranscript,
  }: AgentOptions<UserData>) {
    if (id) {
      this._id = id;
    } else {
      const className = this.constructor.name;
      if (className === 'Agent') {
        this._id = 'default_agent';
      } else {
        this._id = className
          .replace(/([A-Z])/g, '_$1')
          .toLowerCase()
          .replace(/^_/, '');
      }
    }

    this._instructions = instructions;
    this._toolCtx = toToolContext(tools) ?? ToolContext.empty<UserData>();
    this._chatCtx = chatCtx
      ? chatCtx.copy({
          toolCtx: this._toolCtx,
        })
      : ChatContext.empty();

    const resolvedTurnHandling = migrateTurnHandling({
      turnDetection,
      allowInterruptions,
      turnHandling,
    });
    this._turnHandling =
      Object.keys(resolvedTurnHandling).length > 0 ? resolvedTurnHandling : undefined;
    this._asyncToolOptions = toolHandling?.asyncOptions;

    this._vad = vad;

    if (typeof stt === 'string') {
      this._stt = InferenceSTT.fromModelString(stt);
    } else {
      this._stt = stt;
    }

    if (typeof llm === 'string') {
      this._llm = InferenceLLM.fromModelString(llm);
    } else if (llm instanceof DuplexModel) {
      this._llm = new DuplexRealtimeAdapter(llm);
    } else {
      this._llm = llm;
    }

    if (typeof tts === 'string') {
      this._tts = InferenceTTS.fromModelString(tts);
    } else {
      this._tts = tts;
    }

    this._minConsecutiveSpeechDelay = minConsecutiveSpeechDelay;
    this._useTtsAlignedTranscript = useTtsAlignedTranscript;
    this._expressive = expressive;

    this._agentActivity = undefined;
  }

  get vad(): VAD | undefined {
    return this._vad ?? undefined;
  }

  get stt(): STT | undefined {
    return this._stt ?? undefined;
  }

  get llm(): LLM | RealtimeModel | undefined {
    return this._llm ?? undefined;
  }

  /** The running duplex provider session, for provider-specific APIs. */
  get duplexSession(): DuplexSession {
    const session = this.getActivityOrThrow().realtimeLLMSession;
    if (!(session instanceof DuplexRealtimeSession)) {
      throw new Error('no duplex session, this agent is not running a DuplexModel');
    }
    return session.duplexSession;
  }

  get tts(): TTS | undefined {
    return this._tts ?? undefined;
  }

  /** Expressive TTS delivery for this agent, or `undefined` to use the session value. */
  get expressive(): boolean | ExpressiveOptions | undefined {
    return this._expressive;
  }

  get useTtsAlignedTranscript(): boolean | undefined {
    return this._useTtsAlignedTranscript;
  }

  get chatCtx(): ReadonlyChatContext {
    return new ReadonlyChatContext(this._chatCtx.items);
  }

  get id(): string {
    return this._id;
  }

  get instructions(): string | Instructions {
    return this._instructions;
  }

  get toolCtx(): ToolContext<UserData> {
    return this._toolCtx.copy();
  }

  get session(): AgentSession<UserData> {
    return this.getActivityOrThrow().agentSession as AgentSession<UserData>;
  }

  get turnHandling(): Partial<TurnHandlingOptions> | undefined {
    return this._turnHandling;
  }

  get minConsecutiveSpeechDelay(): number | undefined {
    return this._minConsecutiveSpeechDelay;
  }

  async onEnter(): Promise<void> {}

  async onExit(): Promise<void> {}

  async transcriptionNode(
    text: ReadableStream<string | TimedString> | AsyncIterable<string | TimedString>,
    modelSettings: ModelSettings,
  ): Promise<ReadableStream<string | TimedString> | null> {
    return Agent.default.transcriptionNode(this, text, modelSettings);
  }

  async onUserTurnCompleted(_chatCtx: ChatContext, _newMessage: ChatMessage): Promise<void> {}

  async onUserTurnExceeded(ev: UserTurnExceededEvent): Promise<void> {
    await this.session.generateReply({
      userInput: ev.transcript,
      instructions:
        'The user has been speaking too long without giving a chance to reply. ' +
        'Politely cut in with a short reply or notice. Keep it short since the user cannot interrupt it.',
      allowInterruptions: false,
      toolChoice: 'none',
      inputModality: 'audio',
    });
  }

  async sttNode(
    audio: ReadableStream<AudioFrame> | AsyncIterable<AudioFrame>,
    modelSettings: ModelSettings,
  ): Promise<ReadableStream<SpeechEvent | string> | null> {
    return Agent.default.sttNode(this, audio, modelSettings);
  }

  async llmNode(
    chatCtx: ChatContext,
    toolCtx: ToolContext,
    modelSettings: ModelSettings,
  ): Promise<ReadableStream<ChatChunk | string | FlushSentinel> | null> {
    return Agent.default.llmNode(this, chatCtx, toolCtx, modelSettings);
  }

  async ttsNode(
    text: ReadableStream<string> | AsyncIterable<string>,
    modelSettings: ModelSettings,
  ): Promise<ReadableStream<AudioFrame> | null> {
    return Agent.default.ttsNode(this, text, modelSettings);
  }

  async realtimeAudioOutputNode(
    audio: ReadableStream<AudioFrame> | AsyncIterable<AudioFrame>,
    modelSettings: ModelSettings,
  ): Promise<ReadableStream<AudioFrame> | null> {
    return Agent.default.realtimeAudioOutputNode(this, audio, modelSettings);
  }

  // realtime_audio_output_node

  getActivityOrThrow(): AgentActivity {
    if (!this._agentActivity) {
      throw new Error('Agent activity not found');
    }
    return this._agentActivity;
  }

  async updateChatCtx(chatCtx: ChatContext): Promise<void> {
    if (!this._agentActivity) {
      this._chatCtx = chatCtx.copy({ toolCtx: this.toolCtx });
      return;
    }

    await this._agentActivity.updateChatCtx(chatCtx);
  }

  async updateInstructions(instructions: string | Instructions): Promise<void> {
    if (!this._agentActivity) {
      this._instructions = instructions;
      return;
    }

    await this._agentActivity.updateInstructions(instructions);
  }

  async updateOptions(options: AgentUpdateOptions = {}): Promise<void> {
    const resolved: AgentUpdateOptions = { ...options };
    if (typeof resolved.stt === 'string') {
      resolved.stt = InferenceSTT.fromModelString(resolved.stt);
    }
    if (typeof resolved.llm === 'string') {
      resolved.llm = InferenceLLM.fromModelString(resolved.llm);
    } else if (resolved.llm instanceof DuplexModel) {
      resolved.llm = new DuplexRealtimeAdapter(resolved.llm);
    }
    if (typeof resolved.tts === 'string') {
      resolved.tts = InferenceTTS.fromModelString(resolved.tts);
    }

    if (!this._agentActivity) {
      if (resolved.stt !== undefined) this._stt = resolved.stt as STT | null;
      if (resolved.vad !== undefined) this._vad = resolved.vad as VAD | null;
      if (resolved.llm !== undefined) {
        this._llm = resolved.llm as LLM | RealtimeModel | null;
      }
      if (resolved.tts !== undefined) this._tts = resolved.tts as TTS | null;
      if (resolved.expressive !== undefined) this._expressive = resolved.expressive;
      return;
    }

    await this._agentActivity.updateModels(resolved);
    if (resolved.expressive !== undefined) this._expressive = resolved.expressive;
  }

  // TODO(parity): Add when AgentConfigUpdate is ported to ChatContext.
  async updateTools(tools: ToolContextLike<UserData>): Promise<void> {
    if (!this._agentActivity) {
      this._toolCtx = toToolContext(tools);
      this._chatCtx = this._chatCtx.copy({ toolCtx: this._toolCtx });
      return;
    }

    await this._agentActivity.updateTools(tools);
  }

  static default = {
    async sttNode(
      agent: Agent,
      audio: ReadableStream<AudioFrame> | AsyncIterable<AudioFrame>,
      _modelSettings: ModelSettings,
    ): Promise<ReadableStream<SpeechEvent | string> | null> {
      const input = audio instanceof ReadableStream ? audio : toStream(audio);
      const activity = agent.getActivityOrThrow();
      if (!activity.stt) {
        throw new Error('sttNode called but no STT node is available');
      }

      let wrappedStt = activity.stt;

      if (!wrappedStt.capabilities.streaming) {
        const vad = agent.vad || activity.vad;
        if (!vad) {
          throw new Error(
            'STT does not support streaming, add a VAD to the AgentTask/VoiceAgent to enable streaming',
          );
        }
        wrappedStt = new STTStreamAdapter(wrappedStt, vad);
      }

      const connOptions = activity.agentSession.connOptions.sttConnOptions;
      const stream = wrappedStt.stream({ connOptions });

      // Set startTimeOffset to provide linear timestamps across reconnections
      const audioInputStartedAt =
        activity.inputStartedAt ?? // Use input started at proxied from AudioRecognition if available
        activity.agentSession._recorderIO?.recordingStartedAt ?? // Fallback to recording start time if available
        activity.agentSession._startedAt ?? // Fallback to session start time
        Date.now(); // Fallback to current time

      stream.startTimeOffset = (Date.now() - audioInputStartedAt) / 1000;

      stream.updateInputStream(input);

      let cleaned = false;
      const cleanup = () => {
        if (cleaned) return;
        cleaned = true;
        stream.detachInputStream();
        stream.close();
      };

      return new ReadableStream({
        async start(controller) {
          try {
            for await (const event of stream) {
              controller.enqueue(event);
            }
            controller.close();
          } finally {
            // Always clean up the STT stream, whether it ends naturally or is cancelled
            cleanup();
          }
        },
        cancel() {
          cleanup();
        },
      });
    },

    async llmNode(
      agent: Agent,
      chatCtx: ChatContext,
      toolCtx: ToolContext,
      modelSettings: ModelSettings,
    ): Promise<ReadableStream<ChatChunk | string> | null> {
      const activity = agent.getActivityOrThrow();
      if (!activity.llm) {
        throw new Error('llmNode called but no LLM node is available');
      }

      if (!(activity.llm instanceof LLM)) {
        throw new Error(
          'llmNode should only be used with LLM (non-multimodal/realtime APIs) nodes',
        );
      }

      const { toolChoice } = modelSettings;
      const connOptions = activity.agentSession.connOptions.llmConnOptions;

      // parallelToolCalls is not passed here - it will use the value from LLM's modelOptions
      // This allows users to configure it via: new inference.LLM({ modelOptions: { parallel_tool_calls: false } })
      const stream = activity.llm.chat({
        chatCtx,
        toolCtx,
        toolChoice,
        connOptions,
      });

      let cleaned = false;
      const cleanup = () => {
        if (cleaned) return;
        cleaned = true;
        stream.close();
      };

      return new ReadableStream({
        async start(controller) {
          try {
            for await (const chunk of stream) {
              controller.enqueue(chunk);
            }
            controller.close();
          } finally {
            cleanup();
          }
        },
        cancel() {
          cleanup();
        },
      });
    },

    async ttsNode(
      agent: Agent,
      text: ReadableStream<string> | AsyncIterable<string>,
      _modelSettings: ModelSettings,
    ): Promise<ReadableStream<AudioFrame> | null> {
      const input = text instanceof ReadableStream ? text : toStream(text);
      const activity = agent.getActivityOrThrow();
      if (!activity.tts) {
        throw new Error('ttsNode called but no TTS node is available');
      }

      const expressiveActive = activity._resolveExpressiveOptions() !== undefined;
      let wrappedTts = activity.tts;

      if (!activity.tts.capabilities.streaming) {
        wrappedTts = new TTSStreamAdapter(
          wrappedTts,
          // markup only exists in the stream when expressive is active. Python also
          // passes retain_format here, but that predates expressive mode and is a
          // separate gap — turning it on would change tokenization for every
          // non-streaming TTS plugin, none of which can be expressive today.
          new BasicSentenceTokenizer({ xmlAware: expressiveActive }),
        );
      }

      // Mark whether expressive is active for this synthesis, synchronously just before
      // stream() snapshots it. Doing it here (the single synthesis choke point for both
      // generateReply and say()) scopes it to this turn rather than leaving stale state on
      // the instance. The provider's chunk defaults then drive the TTS's input tokenizer.
      activity.tts._setExpressive(expressiveActive);

      const connOptions = activity.agentSession.connOptions.ttsConnOptions;
      const stream = wrappedTts.stream({ connOptions });
      stream.updateInputStream(input);

      let cleaned = false;
      const cleanup = async () => {
        if (cleaned) return;
        cleaned = true;
        stream.close();
        await input.cancel('tts node cleanup').catch(() => {});
        if (wrappedTts !== activity.tts) {
          await wrappedTts.close();
        }
      };

      return new ReadableStream({
        async start(controller) {
          try {
            for await (const chunk of stream) {
              if (chunk === SynthesizeStream.END_OF_STREAM) {
                break;
              }
              // Attach timed transcripts to frame.userdata
              if (chunk.timedTranscripts && chunk.timedTranscripts.length > 0) {
                chunk.frame.userdata[USERDATA_TIMED_TRANSCRIPT] = chunk.timedTranscripts;
              }
              controller.enqueue(chunk.frame);
            }
            controller.close();
          } finally {
            await cleanup();
          }
        },
        cancel() {
          return cleanup();
        },
      });
    },

    async transcriptionNode(
      agent: Agent,
      text: ReadableStream<string | TimedString> | AsyncIterable<string | TimedString>,
      _modelSettings: ModelSettings,
    ): Promise<ReadableStream<string | TimedString> | null> {
      return text instanceof ReadableStream ? text : toStream(text);
    },

    async realtimeAudioOutputNode(
      _agent: Agent,
      audio: ReadableStream<AudioFrame> | AsyncIterable<AudioFrame>,
      _modelSettings: ModelSettings,
    ): Promise<ReadableStream<AudioFrame> | null> {
      return audio instanceof ReadableStream ? audio : toStream(audio);
    },
  };
}

export interface AgentTaskOptions<UserData = any> extends AgentOptions<UserData> {
  preserveFunctionCallHistory?: boolean;
}

export class AgentTask<ResultT = unknown, UserData = any> extends Agent<UserData> {
  private started = false;
  private future = new Future<ResultT>();
  private readonly inactive = new Event();
  private _preserveFunctionCallHistory: boolean;

  /** @internal */
  _oldAgent?: Agent;

  #logger = log();

  static create<ResultT = unknown, UserData = unknown>(
    options: AgentTaskCreateOptions<ResultT, UserData>,
  ): AgentTask<ResultT, UserData> {
    return createAgentTaskV2<ResultT, UserData>(AgentTask, options);
  }

  constructor(options: AgentTaskOptions<UserData>) {
    const { preserveFunctionCallHistory = false, ...rest } = options;
    super(rest);
    this._preserveFunctionCallHistory = preserveFunctionCallHistory;
    this.inactive.set();
  }

  get done(): boolean {
    return this.future.done;
  }

  /** @internal */
  async _waitForInactive(): Promise<void> {
    await this.inactive.wait();
  }

  complete(result: ResultT | Error): void {
    if (this.future.done) {
      throw new Error(`${this.constructor.name} is already done`);
    }

    if (result instanceof Error) {
      this.future.reject(result);
    } else {
      this.future.resolve(result);
    }

    const speechHandle = speechHandleStorage.getStore();
    if (speechHandle) {
      speechHandle._maybeRunFinalOutput = result;
    }
  }

  async run(): Promise<ResultT> {
    if (this.started) {
      throw new Error(
        `Task ${this.constructor.name} has already started and cannot be awaited multiple times`,
      );
    }
    this.started = true;

    const currentTask = Task.current();
    if (!currentTask) {
      throw new Error(`${this.constructor.name} must be executed inside a Task context`);
    }

    const taskInfo = _getActivityTaskInfo(currentTask);
    if (!taskInfo || !taskInfo.inlineTask) {
      throw new Error(
        `${this.constructor.name} should only be awaited inside function tools or the onEnter/onExit methods of an Agent`,
      );
    }

    const speechHandle = speechHandleStorage.getStore();
    const oldActivity = agentActivityStorage.getStore();
    if (!oldActivity) {
      throw new Error(`${this.constructor.name} must be executed inside an AgentActivity context`);
    }

    // A non-blocking tool (one that called ctx.update) detaches from its speech
    // task, so that task completes before a later ctx.foreground/AgentTask does.
    // Binding the guard below to the already-finished task would fire it
    // immediately and tear the AgentTask down; the tool's still-running promise
    // and the foreground hold keep it alive instead.
    const ownerIsNonBlocking =
      taskInfo.functionCall?.extra.__livekit_agents_tool_non_blocking === true;
    const oldAgent = oldActivity.agent;
    this._oldAgent = oldAgent;
    const session = oldActivity.agentSession;

    const blockedTasks: Task<any>[] = [currentTask];
    const onEnterTask = oldActivity._onEnterTask;

    if (onEnterTask && !onEnterTask.done && onEnterTask !== currentTask) {
      blockedTasks.push(onEnterTask);
    }

    this.inactive.clear();
    try {
      return await oldActivity._withInlineTaskSlot({
        speechHandle,
        blockedTasks,
        fn: async () => {
          if (!ownerIsNonBlocking) {
            currentTask.addDoneCallback(() => {
              if (this.future.done) return;

              // If the Task finished before the AgentTask was completed, complete it with an error.
              this.#logger.error(
                `The Task finished before ${this.constructor.name} was completed.`,
              );
              this.complete(
                new Error(`The Task finished before ${this.constructor.name} was completed.`),
              );
            });
          }

          if (
            taskInfo.functionCall &&
            oldActivity.llm instanceof RealtimeModel &&
            !oldActivity.llm.capabilities.manualFunctionCalls
          ) {
            this.#logger.error(
              `Realtime model does not support resuming function calls from chat context, ` +
                `using AgentTask inside a function tool may have unexpected behavior.`,
            );
          }

          await session._updateActivity(this, {
            previousActivity: 'pause',
            newActivity: 'start',
            blockedTasks,
            waitOnEnter: false,
          });

          if (!this._agentActivity && !this.done) {
            this.complete(
              new ToolError(`activity did not start for ${this.id}, likely due to session closing`),
            );
          }

          let runState = session._globalRunState;
          let pendingOnEnterTask: Task<void> | undefined;
          const taskOnEnter = this._agentActivity?._onEnterTask;
          if (taskOnEnter) {
            if (runState && !runState.done()) {
              // Keep the run alive until onEnter has registered any speech it creates.
              runState._watchHandle(taskOnEnter);
              pendingOnEnterTask = taskOnEnter;
            } else {
              await taskOnEnter.result;
            }
          }

          const suspendedHandles: Array<SpeechHandle | Task<any>> = [];
          if (runState && !runState.done()) {
            const suspendHandle = (handle: SpeechHandle | Task<any>) => {
              if (runState!._unwatchHandle(handle)) {
                suspendedHandles.push(handle);
              }
            };

            if (speechHandle) suspendHandle(speechHandle);
            for (const blockedTask of blockedTasks) {
              suspendHandle(blockedTask);
            }
            if (suspendedHandles.length > 0) {
              runState._markDoneIfNeeded();
            }
          }

          try {
            return await this.future.await;
          } finally {
            // The active RunResult may have changed while the task waited for user input.
            runState = session._globalRunState;
            if (runState && !runState.done()) {
              for (const handle of suspendedHandles) {
                runState._watchHandle(handle);
              }
            }

            if (pendingOnEnterTask) {
              try {
                await pendingOnEnterTask.result;
              } catch (error) {
                this.#logger.error(
                  { error },
                  `error in onEnter task of agent ${this.constructor.name}`,
                );
              }
            }

            if (session._closing && this._agentActivity === undefined) {
              // The activity never started because close owns the previous activity.
            } else if (session.currentAgent !== this) {
              this.#logger.warn(
                `${this.constructor.name} completed, but the agent has changed in the meantime. ` +
                  `Ignoring handoff to the previous agent, likely due to AgentSession.updateAgent being invoked.`,
              );
              // Shutdown closes the parent after this task returns. Closing it here
              // would wait for the tool that is currently awaiting this task.
              if (!session._closing) await oldActivity.close();
            } else {
              const mergedChatCtx = oldAgent._chatCtx.merge(this._chatCtx, {
                excludeFunctionCall: !this._preserveFunctionCallHistory,
                excludeInstructions: true,
              });
              oldAgent._chatCtx.items = mergedChatCtx.items;

              await session._updateActivity(oldAgent, {
                previousActivity: 'close',
                newActivity: 'resume',
                waitOnEnter: false,
              });
            }
          }
        },
      });
    } finally {
      this.inactive.set();
    }
  }
}
